import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Volume2, VolumeX } from 'lucide-react';

import { JourneyMap } from '../../sub/mcsa/journey/JourneyMap';
import { JourneyWorldIntro } from '../../sub/mcsa/journey/JourneyWorldIntro';
import { JourneyQuestion } from '../../sub/mcsa/journey/JourneyQuestion';
import { JourneyTransition, JourneyAffirm } from '../../sub/mcsa/journey/JourneyTransition';
import { JourneyGameHost } from '../../sub/mcsa/journey/JourneyGameHost';
import { JourneyWildcard } from '../../sub/mcsa/journey/JourneyWildcard';
import { JourneyFinish } from '../../sub/mcsa/journey/JourneyFinish';

import worldsData from '../../../data/mcsa/worlds.json';
import questionsData from '../../../data/mcsa/questions.json';
import gamesData from '../../../data/mcsa/games.json';
import wildcardsData from '../../../data/mcsa/wildcards.json';

import {
    IMcsaDualAnswer,
    IMcsaGameCatalogItem,
    IMcsaQuestion,
    IMcsaWildcard,
    IMcsaWorld,
    TMcsaAnswer,
    TMcsaWorldKey,
} from '../../../types/mcsa.interface';
import {
    getCurrentWorld,
    getWorldGames,
    getWorldStatus,
    isQuestionAnswered,
    loadMcsaJourneyProgress,
    saveMcsaJourneyProgress,
} from '../../../helpers/mcsa-journey.helper';
import { MCSA_SHOP_SECONDS } from '../../../helpers/mcsa-journey.copy';
import { playMcsaSfx, setMcsaMuted } from '../../../helpers/mcsa-sfx.helper';
import { mcsaJourneyPath, parseMcsaJourneyPath } from '../../../helpers/mcsa-journey.routes';

const MUTE_STORAGE_KEY = 'ms_muted';
const WORLDS = worldsData as IMcsaWorld[];
const QUESTIONS = questionsData as Record<TMcsaWorldKey, IMcsaQuestion[]>;
const GAMES = gamesData as Record<string, IMcsaGameCatalogItem>;
const WILDCARDS = wildcardsData as IMcsaWildcard[];

const readMuted = () => {
    try {
        return localStorage.getItem(MUTE_STORAGE_KEY) === '1';
    } catch {
        return false;
    }
};

export const MCSAJourney = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const route = useMemo(() => parseMcsaJourneyPath(location.pathname), [location.pathname]);
    const saved = useMemo(() => loadMcsaJourneyProgress(), []);

    const [completedKeys, setCompletedKeys] = useState<TMcsaWorldKey[]>(saved.completedKeys);
    const [muted, setMuted] = useState(readMuted);
    const [answers, setAnswers] = useState(saved.answers);
    const [shopQueue, setShopQueue] = useState<number[]>(saved.shopQueue);
    const [secondsLeft, setSecondsLeft] = useState(MCSA_SHOP_SECONDS);
    const [ringPct, setRingPct] = useState(0);
    const [wildcardSel, setWildcardSel] = useState<number | null>(saved.wildcardSel);
    const [wildcardRevealed, setWildcardRevealed] = useState(saved.wildcardRevealed);
    const [shopTick, setShopTick] = useState(0);
    const shopAdvancing = useRef(false);

    const view = route.view;
    const questionIndex = route.questionIndex;
    const gamePhase = route.gamePhase;
    const gameIndex = route.gameIndex;

    const currentWorld = useMemo(
        () => getCurrentWorld(WORLDS, completedKeys),
        [completedKeys],
    );

    const activeWorld = useMemo(
        () => WORLDS.find(world => world.key === route.worldKey) ?? null,
        [route.worldKey],
    );

    const questions = useMemo(
        () => (activeWorld ? QUESTIONS[activeWorld.key] : []),
        [activeWorld],
    );
    const worldAnswers = activeWorld ? answers[activeWorld.key] : {};
    const activeGames = useMemo(
        () => (activeWorld ? getWorldGames(activeWorld, gamePhase) : []),
        [activeWorld, gamePhase],
    );
    const activeGame = activeGames[gameIndex] ? GAMES[activeGames[gameIndex].key] : undefined;
    const wildcard = activeWorld
        ? WILDCARDS.find(item => item.key === activeWorld.wildcard_key) ?? null
        : null;

    useLayoutEffect(() => {
        window.scrollTo(0, 0);
        const scroller = document.querySelector<HTMLElement>(
            '.mcsa_journey_wildcard, .mcsa_journey_intro, .mcsa_journey_stage, .mcsa_journey_finish',
        );
        if (scroller) scroller.scrollTop = 0;
    }, [location.pathname]);

    useEffect(() => {
        setMcsaMuted(muted);
    }, [muted]);

    useEffect(() => {
        saveMcsaJourneyProgress({
            completedKeys,
            answers,
            shopQueue,
            wildcardSel,
            wildcardRevealed,
        });
    }, [completedKeys, answers, shopQueue, wildcardSel, wildcardRevealed]);

    useEffect(() => {
        if (view === 'map' || view === 'finish') return;

        if (!activeWorld) {
            navigate(mcsaJourneyPath.map(), { replace: true });
            return;
        }

        const status = getWorldStatus(WORLDS, completedKeys, activeWorld.key);
        if (status === 'locked') {
            navigate(mcsaJourneyPath.map(), { replace: true });
            return;
        }

        if (view === 'question' && (questionIndex < 0 || questionIndex >= questions.length)) {
            navigate(mcsaJourneyPath.intro(activeWorld.key), { replace: true });
            return;
        }

        if (view === 'game' && !activeGame) {
            navigate(mcsaJourneyPath.intro(activeWorld.key), { replace: true });
            return;
        }

        if (view === 'wildcard' && !wildcard) {
            navigate(mcsaJourneyPath.map(), { replace: true });
        }
    }, [view, activeWorld, activeGame, completedKeys, questionIndex, questions.length, wildcard, navigate]);

    const goToMap = useCallback(() => {
        playMcsaSfx('click');
        navigate(mcsaJourneyPath.map());
    }, [navigate]);

    const beginQuestions = useCallback((world: IMcsaWorld) => {
        const list = QUESTIONS[world.key];
        if (world.question_mech === 'swipe') {
            const queue = list.map((_, index) => index);
            setShopQueue(queue);
            playMcsaSfx('whoosh');
            navigate(mcsaJourneyPath.question(world.key, queue[0] ?? 0));
            return;
        }
        playMcsaSfx('whoosh');
        navigate(mcsaJourneyPath.question(world.key, 0));
    }, [navigate]);

    const finishTheme = useCallback((world: IMcsaWorld) => {
        const postGames = getWorldGames(world, 'post');
        if (postGames.length) {
            const first = GAMES[postGames[0].key];
            playMcsaSfx('whoosh');
            if (first?.post_q_transition) {
                navigate(mcsaJourneyPath.postQuestionTransition(world.key));
                return;
            }
            navigate(mcsaJourneyPath.game(world.key, 'post', 0));
            return;
        }
        playMcsaSfx('chime');
        navigate(mcsaJourneyPath.affirm(world.key));
    }, [navigate]);

    const handleSelectWorld = useCallback((world: IMcsaWorld) => {
        playMcsaSfx('click');
        navigate(mcsaJourneyPath.intro(world.key));
    }, [navigate]);

    const handleStartWorld = useCallback((world: IMcsaWorld) => {
        const preGames = getWorldGames(world, 'pre');
        if (preGames.length) {
            playMcsaSfx('whoosh');
            navigate(mcsaJourneyPath.game(world.key, 'pre', 0));
            return;
        }
        beginQuestions(world);
    }, [beginQuestions, navigate]);

    const handlePick = useCallback((value: number) => {
        if (!activeWorld) return;
        const worldKey = activeWorld.key;
        if (activeWorld.question_mech === 'swipe' && isQuestionAnswered('swipe', answers[worldKey][questionIndex])) return;

        playMcsaSfx('select');
        setAnswers(current => ({
            ...current,
            [worldKey]: { ...current[worldKey], [questionIndex]: value },
        }));

        if (activeWorld.question_mech === 'swipe') {
            shopAdvancing.current = true;
            setShopTick(tick => tick + 1);
            window.setTimeout(() => {
                setShopQueue(queue => {
                    const next = queue.slice(1);
                    if (next.length === 0) {
                        finishTheme(activeWorld);
                        return [];
                    }
                    shopAdvancing.current = false;
                    navigate(mcsaJourneyPath.question(worldKey, next[0]));
                    return next;
                });
            }, 300);
        }
    }, [activeWorld, answers, questionIndex, finishTheme, navigate]);

    const handleDual = useCallback((field: 'like' | 'risk', value: number) => {
        if (!activeWorld) return;
        playMcsaSfx('select');
        setAnswers(current => {
            const existing = current[activeWorld.key][questionIndex];
            const merged: IMcsaDualAnswer = typeof existing === 'object' && existing ? { ...existing, [field]: value } : { [field]: value };
            return {
                ...current,
                [activeWorld.key]: { ...current[activeWorld.key], [questionIndex]: merged },
            };
        });
    }, [activeWorld, questionIndex]);

    const handleNext = useCallback(() => {
        if (!activeWorld) return;
        const answer = answers[activeWorld.key][questionIndex] as TMcsaAnswer | undefined;
        if (!isQuestionAnswered(activeWorld.question_mech, answer)) return;
        playMcsaSfx('whoosh');
        if (questionIndex >= questions.length - 1) {
            finishTheme(activeWorld);
            return;
        }
        navigate(mcsaJourneyPath.question(activeWorld.key, questionIndex + 1));
    }, [activeWorld, answers, questionIndex, questions.length, finishTheme, navigate]);

    const handleQuestionBack = useCallback(() => {
        if (!activeWorld) return;
        playMcsaSfx('click');
        if (questionIndex <= 0) {
            navigate(mcsaJourneyPath.intro(activeWorld.key));
            return;
        }
        navigate(mcsaJourneyPath.question(activeWorld.key, questionIndex - 1));
    }, [activeWorld, questionIndex, navigate]);

    const advanceGame = useCallback(() => {
        if (!activeWorld) return;
        const list = getWorldGames(activeWorld, gamePhase);
        const nextIndex = gameIndex + 1;

        playMcsaSfx('whoosh');
        if (gamePhase === 'pre') {
            if (nextIndex >= list.length) {
                const catalog = GAMES[list[0]?.key];
                if (catalog?.post_transition) navigate(mcsaJourneyPath.preTransition(activeWorld.key));
                else beginQuestions(activeWorld);
                return;
            }
            navigate(mcsaJourneyPath.game(activeWorld.key, 'pre', nextIndex));
            return;
        }

        if (nextIndex >= list.length) {
            playMcsaSfx('chime');
            navigate(mcsaJourneyPath.affirm(activeWorld.key));
            return;
        }

        const nextGame = GAMES[list[nextIndex].key];
        if (nextGame?.pre_transition) {
            navigate(mcsaJourneyPath.midGameTransition(activeWorld.key, nextIndex));
            return;
        }
        navigate(mcsaJourneyPath.game(activeWorld.key, 'post', nextIndex));
    }, [activeWorld, gamePhase, gameIndex, beginQuestions, navigate]);

    const handleAffirmContinue = useCallback(() => {
        if (!activeWorld) return;
        const nextCompleted = completedKeys.includes(activeWorld.key)
            ? completedKeys
            : [...completedKeys, activeWorld.key];
        setCompletedKeys(nextCompleted);

        playMcsaSfx('teaser');
        if (activeWorld.wildcard_key) {
            setWildcardSel(null);
            setWildcardRevealed(false);
            navigate(mcsaJourneyPath.wildcard(activeWorld.key));
            return;
        }

        const isLast = activeWorld.key === WORLDS[WORLDS.length - 1].key;
        playMcsaSfx(isLast ? 'chime' : 'whoosh');
        window.setTimeout(() => playMcsaSfx('unlock'), 700);
        navigate(isLast ? mcsaJourneyPath.finish() : mcsaJourneyPath.map());
    }, [activeWorld, completedKeys, navigate]);

    const handleWildcardContinue = useCallback(() => {
        playMcsaSfx('whoosh');
        window.setTimeout(() => playMcsaSfx('unlock'), 700);
        setWildcardSel(null);
        setWildcardRevealed(false);
        navigate(mcsaJourneyPath.map());
    }, [navigate]);

    useEffect(() => {
        shopAdvancing.current = false;
    }, [questionIndex, view]);

    useEffect(() => {
        if (view !== 'question' || activeWorld?.question_mech !== 'swipe') return;

        if (shopQueue.length === 0) {
            const unanswered = questions
                .map((_, index) => index)
                .filter(index => !isQuestionAnswered('swipe', answers[activeWorld.key][index]));
            setShopQueue(unanswered.length ? unanswered : questions.map((_, index) => index));
        }
    }, [view, activeWorld, questions, answers, shopQueue.length]);

    useEffect(() => {
        if (view !== 'question' || activeWorld?.question_mech !== 'swipe' || shopAdvancing.current) return;

        const started = Date.now();
        const total = MCSA_SHOP_SECONDS * 1000;
        setSecondsLeft(MCSA_SHOP_SECONDS);
        setRingPct(0);

        const timer = window.setInterval(() => {
            const elapsed = Date.now() - started;
            setRingPct(Math.min(1, elapsed / total));
            setSecondsLeft(Math.max(0, Math.ceil((total - elapsed) / 1000)));
            if (elapsed >= total) {
                window.clearInterval(timer);
                setShopQueue(queue => {
                    const current = queue[0];
                    const rest = queue.slice(1);
                    const next = current === undefined ? rest : [...rest, current];
                    playMcsaSfx('skip');
                    if (next.length === 0) {
                        if (activeWorld) finishTheme(activeWorld);
                        return [];
                    }
                    if (activeWorld) navigate(mcsaJourneyPath.question(activeWorld.key, next[0]));
                    return next;
                });
            }
        }, 90);

        return () => window.clearInterval(timer);
    }, [view, questionIndex, activeWorld, finishTheme, shopTick, navigate]);

    const handleToggleMute = () => {
        setMuted(current => {
            const next = !current;
            setMcsaMuted(next);
            try {
                localStorage.setItem(MUTE_STORAGE_KEY, next ? '1' : '0');
            } catch {
                // ignore
            }
            if (!next) window.setTimeout(() => playMcsaSfx('click'), 0);
            return next;
        });
    };

    const preTransition = activeWorld ? GAMES[getWorldGames(activeWorld, 'pre')[0]?.key]?.post_transition : undefined;
    const postQTransition = activeWorld ? GAMES[getWorldGames(activeWorld, 'post')[0]?.key]?.post_q_transition : undefined;
    const midTransition = activeGame?.pre_transition ?? (
        activeWorld ? GAMES[getWorldGames(activeWorld, 'post')[gameIndex]?.key]?.pre_transition : undefined
    );

    return (
        <section className='mcsa_journey__page'>
            <button
                type='button'
                className='mcsa_journey__mute'
                title={muted ? 'Unmute' : 'Mute'}
                onClick={handleToggleMute}
            >
                {muted ? <VolumeX /> : <Volume2 />}
            </button>

            {view === 'map' && (
                <JourneyMap
                    worlds={WORLDS}
                    completedKeys={completedKeys}
                    currentWorld={currentWorld}
                    onSelectWorld={handleSelectWorld}
                />
            )}

            {view === 'intro' && activeWorld && (
                <JourneyWorldIntro
                    world={activeWorld}
                    worldCount={WORLDS.length}
                    onStart={handleStartWorld}
                    onBack={goToMap}
                />
            )}

            {view === 'question' && activeWorld && (
                <JourneyQuestion
                    world={activeWorld}
                    questions={questions}
                    questionIndex={questionIndex}
                    answers={worldAnswers}
                    shopRemaining={shopQueue.length}
                    secondsLeft={secondsLeft}
                    ringPct={ringPct}
                    onPick={handlePick}
                    onDual={handleDual}
                    onNext={handleNext}
                    onBack={handleQuestionBack}
                    onBackToMap={goToMap}
                />
            )}

            {view === 'pre_game_transition' && activeWorld && preTransition && (
                <JourneyTransition
                    world={activeWorld}
                    copy={preTransition}
                    onContinue={() => beginQuestions(activeWorld)}
                    onBack={goToMap}
                />
            )}

            {view === 'post_q_transition' && activeWorld && postQTransition && (
                <JourneyTransition
                    world={activeWorld}
                    copy={postQTransition}
                    onContinue={() => {
                        playMcsaSfx('whoosh');
                        navigate(mcsaJourneyPath.game(activeWorld.key, 'post', 0));
                    }}
                    onBack={goToMap}
                />
            )}

            {view === 'mid_game_transition' && activeWorld && midTransition && (
                <JourneyTransition
                    world={activeWorld}
                    copy={midTransition}
                    onContinue={() => {
                        playMcsaSfx('whoosh');
                        navigate(mcsaJourneyPath.game(activeWorld.key, 'post', gameIndex));
                    }}
                    onBack={goToMap}
                />
            )}

            {view === 'game' && activeWorld && activeGame && (
                <JourneyGameHost
                    world={activeWorld}
                    game={activeGame}
                    gameNum={gameIndex + 1}
                    gameTotal={activeGames.length}
                    muted={muted}
                    onSkip={advanceGame}
                    onComplete={advanceGame}
                    onBackToMap={goToMap}
                />
            )}

            {view === 'affirm' && activeWorld && (
                <JourneyAffirm
                    world={activeWorld}
                    isLast={activeWorld.key === WORLDS[WORLDS.length - 1].key}
                    onContinue={handleAffirmContinue}
                />
            )}

            {view === 'wildcard' && wildcard && (
                <JourneyWildcard
                    wildcard={wildcard}
                    selected={wildcardSel}
                    revealed={wildcardRevealed}
                    onPick={index => {
                        if (wildcardRevealed) return;
                        playMcsaSfx('select');
                        setWildcardSel(index);
                        setWildcardRevealed(true);
                    }}
                    onContinue={handleWildcardContinue}
                />
            )}

            {view === 'finish' && (
                <JourneyFinish
                    onBackToMap={goToMap}
                    onViewResults={() => navigate('/mcsa/report')}
                />
            )}
        </section>
    );
};
